import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Caps `staggerDelay` at 100ms on `CanvasTextAsset`s that use the highlight box+mask wipe
 * effect. After 9.240 froze the old percentage stagger into fixed milliseconds, some of these
 * scenes have very large per-line staggers that desync from the fixed-delay box wipe; clamping
 * to 100ms keeps the highlighted text in step with the wipe.
 *
 * An asset is affected when ALL hold:
 *   - classType is `CanvasTextAsset`
 *   - `highlightStyle.renderHighlighted` is true
 *   - `highlightStyle.graphics` has a box (type `box`, `asMask` false) AND a mask (type `box`,
 *     `asMask` true), each with an `xProgress` animation
 *   - `highlightStyle.animationConfigurations` is non-empty
 *   - some animation anywhere on the asset has `staggerDelay > 100`
 *
 * When matched, every animation on the asset (both styles, plus per-graphic animations) has its
 * `staggerDelay` clamped to at most 100.
 */

const STAGGER_DELAY_CAP = 100;

const STYLE_FIELDS = ['normalStyle', 'highlightStyle'] as const;

function styleAnimations(asset: Immutable.Map<string, unknown>): Immutable.Map<string, unknown>[] {
    const animations: Immutable.Map<string, unknown>[] = [];
    STYLE_FIELDS.forEach((styleField) => {
        const style = asset.get(styleField) as Immutable.Map<string, unknown> | undefined;
        if (!style) return;

        const styleAnims = style.get('animationConfigurations') as
            | Immutable.List<Immutable.Map<string, unknown>>
            | undefined;
        if (styleAnims) styleAnims.forEach((a) => animations.push(a));

        const graphics = style.get('graphics') as
            | Immutable.List<Immutable.Map<string, unknown>>
            | undefined;
        if (graphics) {
            graphics.forEach((graphic) => {
                const graphicAnims = graphic.get('animationConfigurations') as
                    | Immutable.List<Immutable.Map<string, unknown>>
                    | undefined;
                if (graphicAnims) graphicAnims.forEach((a) => animations.push(a));
            });
        }
    });
    return animations;
}

function graphicHasXProgress(graphic: Immutable.Map<string, unknown>): boolean {
    if (graphic.get('type') !== 'box') return false;
    const animations = graphic.get('animationConfigurations') as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    if (!animations) return false;
    return animations.some((a) => a.get('property') === 'xProgress');
}

function assetShouldBeCapped(asset: Immutable.Map<string, unknown>): boolean {
    if (asset.get('classType') !== 'CanvasTextAsset') return false;

    const highlightStyle = asset.get('highlightStyle') as
        | Immutable.Map<string, unknown>
        | undefined;
    if (!highlightStyle) return false;
    if (highlightStyle.get('renderHighlighted') !== true) return false;

    const highlightAnims = highlightStyle.get('animationConfigurations') as
        | Immutable.List<unknown>
        | undefined;
    if (!highlightAnims || highlightAnims.size === 0) return false;

    const graphics = highlightStyle.get('graphics') as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    if (!graphics) return false;
    const hasBox = graphics.some((g) => g.get('asMask') === false && graphicHasXProgress(g));
    const hasMask = graphics.some((g) => g.get('asMask') === true && graphicHasXProgress(g));
    if (!hasBox || !hasMask) return false;

    return styleAnimations(asset).some(
        (a) => ((a.get('staggerDelay') as number | undefined) ?? 0) > STAGGER_DELAY_CAP,
    );
}

function capAnimationList(map: Immutable.Map<string, unknown>, path: Array<string | number>): void {
    const list = map.getIn(path) as Immutable.List<Immutable.Map<string, unknown>> | undefined;
    if (!list) return;
    list.forEach((_anim, i) => {
        const animPath = [...path, i];
        const anim = map.getIn(animPath) as Immutable.Map<string, unknown>;
        const staggerDelay = (anim.get('staggerDelay') as number | undefined) ?? 0;
        if (staggerDelay > STAGGER_DELAY_CAP) {
            map.setIn([...animPath, 'staggerDelay'], STAGGER_DELAY_CAP);
        }
    });
}

function capAssetIfMatch(
    map: Immutable.Map<string, unknown>,
    assetPath: Array<string | number>,
): void {
    const asset = map.getIn(assetPath) as Immutable.Map<string, unknown> | undefined;
    if (!asset) return;
    if (!assetShouldBeCapped(asset)) return;

    STYLE_FIELDS.forEach((styleField) => {
        const style = asset.get(styleField) as Immutable.Map<string, unknown> | undefined;
        if (!style) return;

        capAnimationList(map, [...assetPath, styleField, 'animationConfigurations']);

        const graphics = style.get('graphics') as Immutable.List<unknown> | undefined;
        if (graphics) {
            graphics.forEach((_graphic, gIndex) => {
                capAnimationList(map, [
                    ...assetPath,
                    styleField,
                    'graphics',
                    gIndex,
                    'animationConfigurations',
                ]);
            });
        }
    });
}

function capChildren(
    map: Immutable.Map<string, unknown>,
    childrenPath: Array<string | number>,
): void {
    const children = map.getIn(childrenPath) as Immutable.List<unknown> | undefined;
    if (!children) return;
    children.forEach((_child, i) => {
        capAssetIfMatch(map, [...childrenPath, i]);
    });
}

export const migrationTo9_241 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            const slides = map.get('slides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (slides) {
                slides.forEach((scene, sceneIdx) => {
                    map.setIn(['slides', sceneIdx], this.migrateScene(scene));
                });
            }
        });
    },

    migrateScene(scene, _isSceneDesign) {
        return scene.withMutations((map) => {
            capChildren(map, ['children']);
            capChildren(map, ['stickers']);

            const subslides = map.get('subslides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (subslides) {
                subslides.forEach((_subslide, subIdx) => {
                    capChildren(map, ['subslides', subIdx, 'children']);
                });
            }

            const presets = map.get('textPositionPresets') as
                | Immutable.Map<string, Immutable.List<unknown>>
                | undefined;
            if (presets) {
                presets.forEach((_assets, position) => {
                    capChildren(map, ['textPositionPresets', position]);
                });
            }
        });
    },
});
